/* eslint-disable camelcase */
import { ReactNode, useMemo } from 'react'
import {
  Mutation,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AxiosError } from 'axios'
import { overlay } from 'overlay-kit'

import { useI18n } from '@ahha/i18n/i18n'
import { TOAST_STATES } from '@ahha/utils/@types/types'
import Dialog from '@ahha/stableComponents/Dialog/Dialog'

import { setUser } from '@/stores/slices/userSlice'

import { API } from '@/api'
import { ErrorResponse } from '@/api/Base/types'
import { useGlobalNotification } from '@/context/notificationContext'

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: AxiosError<ErrorResponse, any>
  }
}

interface QueryProviderProps {
  children: ReactNode
}

const getServerErrorMessage = ({ response }: AxiosError<ErrorResponse>) => {
  const { status_code, message } = response?.data ?? {}

  return message ?? 'Unknown Error'
}

const toastErrorMessage = (error: Error) => {
}

const mutationErrorLogger = (error: Error, mutation: Mutation, variables: unknown) => {
}

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const { addNotification } = useGlobalNotification()
  const dispatch = useDispatch()
  const { t } = useI18n()

  const queryClient = useMemo(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const { enableErrorToast = true } = query.meta ?? {}
        if (!(error instanceof AxiosError)) {
          return
        }
        // FIXME: axios interceptor에서 처리 가능한지 확인
        if (error.response?.data?.unique_code === 100001) {
          dispatch(setUser({
            token: '',
            user: {
              _id: '',
              email: '',
              name: '',
              lastWorkspaceId: '',
              meta: {},
              isSuperUser: false,
            },
            isSignIn: false,
          }))
          API().setToken('')
          window.location.href = '/login'
        }
        const message = getServerErrorMessage(error)
        console.error(message)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, _, mutation) => {
        const { unique_code: uniqueCode } = error?.response?.data || {}

        const { enableErrorToast = true } = mutation.meta ?? {}

        if ((uniqueCode === 400104 || uniqueCode === 100014) && (error.response?.config.method !== 'get')) {
          addNotification({
            title: t('common_noPermission_toast_title'),
            message: t('common_noPermission_toast_message'),
            status: TOAST_STATES.ERROR,
          })
        }

        // 크레딧 부족
        if (uniqueCode === 2000001) {
          overlay.open(({ isOpen, close }) => (
            <Dialog
              height="auto"
              open={isOpen}
              title={t('credit_error_dialog_insufficientCredit_title')}
              content={(<p style={{ fontSize: '0.875rem', lineHeight: '1.375rem' }}>{t('credit_error_dialog_insufficientCredit_message')}</p>)}
              onClose={close}
              showCloseButton
              confirmText={t('common_confirm')}
              confirmAction={close}
            />
          ))
          return
        }

        if (!(error instanceof AxiosError)) {
          return
        }
        const message = getServerErrorMessage(error)
        console.error(message)
        /* if (enableErrorToast) {
          toastErrorMessage(error)
        } */
      },
    }),
    defaultOptions: {
      queries: {
        // TODO: 바로 바뀌는 화면들을 봐야하는 경우가 더 많은것 같아 임시 주석처리
        // staleTime: 30 * 1000,
        retry: (count, error) => {
          const { status } = error?.response ?? {}

          if (status && status >= 500) {
            return false
          }
          return count < 1
        },
        throwOnError: true,
      },
      mutations: {
        throwOnError: (e) => {
          const { unique_code: uniqueCode } = e?.response?.data || {}

          if ((uniqueCode === 400104 || uniqueCode === 100014) && (e.response?.config.method !== 'get')) {
            return false
          }

          // 크레딧 부족 - 크레딧 부족 관련 ui 처리하기 위해 throw 방지
          if (uniqueCode === 2000001) {
            return false
          }

          return true
        },
      },
    },
  }), [])

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      {children}
    </QueryClientProvider>
  )
}
